import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useProjectStore } from "@/lib/project-store";
import { getProjects, getAuthMe } from "@/api/project-api";

/**
 * Redirects from "/" to "/projects/:projectId" using the last-used project
 * or the first available project. Redirects to onboarding if not completed.
 */
export function ProjectRedirect() {
  const { currentProjectId, setCurrentProjectId } = useProjectStore();

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const res = await getAuthMe();
      if (res.error) throw new Error("Failed to fetch user");
      return res.data;
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const res = await getProjects();
      if (res.error) throw new Error("Failed to fetch projects");
      return res.data;
    },
    enabled: meData?.data?.onboardingCompleted !== false,
  });

  if (meLoading || isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (meData?.data && !meData.data.onboardingCompleted) {
    return <Navigate to="/onboarding" replace />;
  }

  const projects = data?.data ?? [];

  if (projects.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen text-muted-foreground">
        No projects found
      </div>
    );
  }

  // Use last-used project if it still exists, otherwise first project
  let targetProjectId: string = currentProjectId ?? "";
  if (!targetProjectId || !projects.some((p) => p.id === targetProjectId)) {
    targetProjectId = projects[0].id;
    setCurrentProjectId(targetProjectId);
  }

  return <Navigate to={`/projects/${targetProjectId}`} replace />;
}
